'use client';

import { type ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';

import { CALENDAR_CHANGE_REASON } from '../../calendar/constants/calendar-change-reason';
import type { DatePickerRootContextInterface } from '../../date-picker';
import type { DateInputPropsInterface } from '../types/date-input.types';

/**
 * Custom hook that handles value management for DateInput components.
 *
 * This hook implements a "tri-mode" value management system that can operate in three modes:
 * 1. Controlled by props - When the component receives a 'value' prop
 * 2. Controlled by context - When used within a DatePicker context
 * 3. Uncontrolled - When neither props nor context control the value
 *
 * The hook automatically detects which mode to use based on the presence of props and context,
 * following a strict priority order (props > context > internal state).
 *
 * @param params - Input parameters containing props and context
 * @param params.props - Props passed to the DateInput component
 * @param params.context - DatePicker context if the component is used within a DatePicker
 * @returns An object containing the final value and onChange handler
 */
export function useDateInputState(params: {
  props: DateInputPropsInterface;
  context: Partial<DatePickerRootContextInterface>;
}) {
  const { props, context } = params;
  const { defaultValue = '', onChange } = props;
  const { datePicker } = context;
  // Initialize internal state with defaultValue for uncontrolled mode
  const [internalValue, setInternalValue] = useState(defaultValue);

  // Determine the mode of operation using 'in' operator to check for property existence
  // This approach correctly identifies controlled components even if value is undefined
  const isControlledByProps = 'value' in props;
  const isControlledByContext = !isControlledByProps && datePicker && 'value' in datePicker;
  const isUncontrolled = !isControlledByProps && !isControlledByContext;

  // Update internal state when defaultValue changes in uncontrolled mode
  // This ensures that the component respects defaultValue changes over time
  useEffect(() => {
    if (isUncontrolled) {
      // oxlint-disable-next-line react/set-state-in-effect -- This API intentionally accepts defaultValue updates.
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isUncontrolled]);

  // Determine the final value to display based on the control mode
  // Priority: props > context > internal state
  const finalValue = useMemo(() => {
    if (isControlledByProps) {
      return props.value;
    }

    if (isControlledByContext) {
      return datePicker?.value;
    }

    return internalValue;
  }, [props.value, datePicker?.value, internalValue, isControlledByContext, isControlledByProps]);

  /**
   * Handle input change events with appropriate behavior based on control mode
   * - For controlled props: Parent component is responsible for updates
   * - For context control: Update via context's onValueChange handler
   * - For uncontrolled: Update internal state
   */
  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const newValue = event.target.value;

      // Always call onChange prop if it exists, regardless of control mode
      // This allows for notification without controlling the component
      onChange?.(event);

      if (isControlledByProps) {
        // Controlled by props - parent handles state updates
        // Empty block is intentional - no action needed here
      } else if (isControlledByContext) {
        // Controlled by context - update via DatePicker context
        // Pass additional metadata about the change source
        datePicker?.onValueChange?.(newValue, {
          reason: CALENDAR_CHANGE_REASON.manualInput,
        });
      } else if (isUncontrolled) {
        // Uncontrolled - manage state internally
        setInternalValue(newValue);
      }
    },
    [onChange, isControlledByProps, isControlledByContext, isUncontrolled, datePicker],
  );

  /*
   * Memoize the returned object so consumers see a stable reference
   * whenever neither `finalValue` nor `handleChange` changes. Without
   * this, every render of the caller would produce a fresh object
   * literal and defeat downstream memoization — see CDS-5649 (FIX-3).
   */
  return useMemo(
    () => ({
      value: finalValue,
      onChange: handleChange,
    }),
    [finalValue, handleChange],
  );
}
